import { useTranslation } from 'react-i18next'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '~/components/ui/dialog'
import { Label } from '~/components/ui/label'
import { useAppStore } from '~/stores/app'
import { DialogId } from '~/types/common'
import type { NgWord } from '~/types/ng-word'

type NgWordDetailDialogProps = {
  item?: NgWord | null
  onEdit: (item: NgWord) => void
  onDelete: (item: NgWord) => void
}

export function NgWordDetailDialog({ item, onEdit, onDelete }: NgWordDetailDialogProps) {
  const { t } = useTranslation()
  const openDialogIds = useAppStore((state) => state.openDialogIds)
  const openDialog = useAppStore((state) => state.openDialog)
  const closeDialog = useAppStore((state) => state.closeDialog)

  const isOpen = openDialogIds.includes(DialogId.DETAIL_NG_WORD)

  if (!item) return null

  const handleEdit = () => {
    closeDialog(DialogId.DETAIL_NG_WORD)
    onEdit(item)
    openDialog(DialogId.UPDATE_NG_WORD)
  }

  const handleDelete = () => {
    closeDialog(DialogId.DETAIL_NG_WORD)
    onDelete(item)
    openDialog(DialogId.DELETE_NG_WORD)
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeDialog(DialogId.DETAIL_NG_WORD)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('ngWords.word')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1">
            <Label className="text-muted-foreground">{t('ngWords.word')}</Label>
            <p className="font-medium">{item.word}</p>
          </div>
          <div className="space-y-1">
            <Label className="text-muted-foreground">{t('common.status')}</Label>
            <div>
              <Badge variant={item.isActive ? 'success' : 'secondary'}>
                {item.isActive ? t('common.active') : t('common.inactive')}
              </Badge>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-muted-foreground">{t('common.createdAt')}</Label>
            <p>{new Date(item.createdAt).toLocaleString()}</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleEdit}>
            {t('common.edit')}
          </Button>
          <Button variant="destructive" onClick={handleDelete}>
            {t('common.delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
